import { createTranslation } from '@choffmeister/i18next'
import { css, SerializedStyles } from '@emotion/core'
import parse from 'html-react-parser'
import * as React from 'react'

import { breakpoints } from '../constants/breakPoints'
import { homepageTestimonials, Testimonial } from '../constants/testimonials'
import QuoteIcon from '../icons/abstract/quote.svg'
import Grid from './Grid'
import Image from './Image'
import SvgIcon from './SvgIcon'
import VideoPopup from './VideoPopup'

interface TestimonialSliderProps {
  heading?: string
  subtitle?: string
  testimonialData?: Testimonial[]
  cssStyles?: SerializedStyles
}

const TestimonialSlider: React.FunctionComponent<TestimonialSliderProps> = ({
  heading,
  subtitle,
  testimonialData = homepageTestimonials,
  cssStyles,
}) => {
  const { useTranslation } = createTranslation(testimonialData)
  const { t } = useTranslation()

  const [currentIndex, setCurrentIndex] = React.useState(0)
  const [sliderTranslateXFactor, setSliderTranslateXFactor] = React.useState(0)

  const sliderDecrementTranslateXLogic = (sliderTranslateXFactor: number) => {
    if (sliderTranslateXFactor === -1) {
      setSliderTranslateXFactor(testimonialData.length - 1)
    } else {
      setSliderTranslateXFactor(sliderTranslateXFactor - 1)
    }
  }

  const sliderIncrementTranslateXLogic = (sliderTranslateXFactor: number) => {
    setSliderTranslateXFactor((sliderTranslateXFactor + 1) % testimonialData.length)
  }

  React.useEffect(() => {
    if (sliderTranslateXFactor === -1) {
      sliderDecrementTranslateXLogic(-1)
    }
    if (sliderTranslateXFactor === testimonialData.length) {
      sliderIncrementTranslateXLogic(testimonialData.length - 1)
    }
  }, [sliderTranslateXFactor])

  return (
    <div className="full-screen-relative blue-bg-block" css={[sliderBlockStyle, cssStyles]}>
      {heading && (
        <h2 className="heading2 center no-margin" css={sliderHeadingStyle}>
          {parse(heading)}
        </h2>
      )}
      {subtitle && <div className="subtitle center">{parse(subtitle)}</div>}

      <div className="full-screen-relative" css={sliderContainerStyle}>
        <div
          css={arrowStyle}
          onClick={() => (
            sliderDecrementTranslateXLogic(sliderTranslateXFactor),
            currentIndex === 0 ? setCurrentIndex(testimonialData.length - 1) : setCurrentIndex(currentIndex - 1)
          )}
        >
          <SvgIcon src="abstract/arrow-left" />
        </div>
        <div css={sliderTrackContainerStyle(sliderTranslateXFactor, testimonialData.length)}>
          {testimonialData.slice(testimonialData.length - 2).map(({ name, image, video, company, icon }, index) => (
            <TestimonialCard
              key={`testimonial-card-${name}`}
              className={currentIndex === testimonialData.length - 1 + index - 1 ? 'centered-card' : ''}
              icon={icon}
              video={
                video
                  ? {
                      ...testimonialData[testimonialData.length - 1 + index - 1].video,
                      imageSrc: t(`${index}.video.imageSrc`),
                      youTubeId: t(`${index}.video.youTubeId`),
                    }
                  : undefined
              }
              quote={t(`${testimonialData.length - 1 + index - 1}.quote`)}
              imageSrc={image}
              name={name}
              position={t(`${testimonialData.length - 1 + index - 1}.position`)}
              company={company}
            />
          ))}
          {testimonialData.map(({ name, image, video, company, icon }, index) => (
            <TestimonialCard
              key={`testimonial-card-${name}`}
              className={currentIndex === index ? 'centered-card' : ''}
              icon={icon}
              video={
                video
                  ? {
                      ...testimonialData[index].video,
                      imageSrc: t(`${index}.video.imageSrc`),
                      youTubeId: t(`${index}.video.youTubeId`),
                    }
                  : undefined
              }
              quote={t(`${index}.quote`)}
              imageSrc={image}
              name={name}
              position={t(`${index}.position`)}
              company={company}
            />
          ))}
          {testimonialData.slice(0, 2).map(({ name, image, video, company, icon }, index) => (
            <TestimonialCard
              key={`testimonial-card-${name}`}
              className={currentIndex === index ? 'centered-card' : ''}
              icon={icon}
              video={
                video
                  ? {
                      ...testimonialData[index].video,
                      imageSrc: t(`${index}.video.imageSrc`),
                      youTubeId: t(`${index}.video.youTubeId`),
                    }
                  : undefined
              }
              quote={t(`${index}.quote`)}
              imageSrc={image}
              name={name}
              position={t(`${index}.position`)}
              company={company}
            />
          ))}
        </div>
        <div
          css={[arrowStyle, rightArrowStyle]}
          onClick={() => (
            setSliderTranslateXFactor((sliderTranslateXFactor + 1) % (testimonialData.length + 1)),
            setCurrentIndex((currentIndex + 1) % testimonialData.length)
          )}
        >
          <SvgIcon src="abstract/arrow-right" />
        </div>
      </div>
    </div>
  )
}

export default TestimonialSlider

interface TestimonialCardProps {
  className?: string
  icon: string
  quote: string
  video?: {
    imageSrc?: string
    imageAlt?: string
    gatsbyImageSrc?: any
    youTubeId?: string
    hippoVideoId?: string
    videoUrl?: string
  }
  imageSrc: string
  name: string
  position: string
  company: string
}

const TestimonialCard: React.FunctionComponent<TestimonialCardProps> = ({
  className,
  icon,
  quote,
  video,
  imageSrc,
  name,
  position,
  company,
}) => {
  return (
    <div className={className} css={testimonialCardContainerStyle}>
      <div css={iconContainerStyle}>
        {video ? <SvgIcon id="icon-above-image" src="abstract/play-button" css={videoPlayIconStyle} /> : <QuoteIcon />}
        <Image src={icon} alt={company} css={quoteIconStyle(icon.includes('g2'))} />
      </div>
      {video ? (
        <div className="gray-bordered-block with-blue-box-shadow" css={youTubeVideoStyle}>
          <VideoPopup video={video} />
        </div>
      ) : (
        <div className="quote">{parse(quote)}</div>
      )}
      <div css={quoteDividerStyle} />
      <Grid columnsTemplate="3.5rem auto" noBreak css={authorStyle}>
        <Image
          className="with-blue-box-shadow"
          src={imageSrc}
          alt={name.replace(/<span>/g, '').replace(/<\/span>/g, '')}
          css={quoteImgStyle}
        />
        <div>
          <div className="strong-body-1">{parse(name)}</div>
          <div className="body-2">{`${position} - ${company}`}</div>
        </div>
      </Grid>
    </div>
  )
}

const testimonialCardContainerStyle = css`
  box-sizing: border-box;
  background-color: #fff;
  min-width: 30rem;
  height: fit-content;
  align-self: center;
  border-radius: 0.5rem;
  margin: 4rem 10vw;
  padding: 2rem;
  opacity: 0.5;
  transform: scale(0.8);
  transition:
    transform 0.6s,
    opacity 1s;

  &.centered-card {
    opacity: 1;
    transform: scale(1);
    box-shadow: rgba(5, 140, 232, 0.1) 0px 0px 20px;
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    margin: 4rem 5vw;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    padding: 1.5rem;
    min-width: calc(100vw - 3rem);
  }
`

const iconContainerStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const videoPlayIconStyle = css`
  width: 1.5rem;
  height: auto;

  path {
    fill: #318eff;
  }
`

const youTubeVideoStyle = css`
  margin-top: 1.5rem;
`

const quoteIconStyle = (isG2logo: boolean) => css`
  width: ${isG2logo ? '2rem' : '5rem'};
  overflow: visible;
  height: auto;

  img {
    height: 100%;
    width: auto;
  }
`

const quoteImgStyle = css`
  box-sizing: border-box;
  border-radius: 50%;
  border: 3px solid #fff;
  overflow: hidden;
  width: 3.5rem;
  height: 3.5rem;
`

const quoteDividerStyle = css`
  margin-top: 2rem;
  width: max(100px, 34%);
  height: 2px;
  background-color: #318eff;
`

const authorStyle = css`
  margin-top: 0.75rem;
`

const sliderBlockStyle = css`
  margin-top: 8rem;
  margin-bottom: 4rem;
  position: relative;
  overflow: hidden;

  @media screen and (max-width: ${breakpoints.medium}px) {
    margin-top: 4rem;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    &.blue-bg-block {
      padding-top: 0;
    }
  }
`

const sliderHeadingStyle = css`
  @media screen and (max-width: ${breakpoints.small}px) {
    margin-top: 4rem !important;
  }
`

const sliderContainerStyle = css`
  position: relative;
`

const sliderTrackContainerStyle = (sliderTransformXFactor: number, arrLength: number) => css`
  display: flex;
  // logic for transform:
  // 50% to shift to center of page
  // 2.5 * (card-width + card-margin) shift to left (2 extra cards are displayed)
  // for each click the track will be shifted by (card-width + card-margin to left / right)
  // same logic for small & medium with amended values (card-width + card-margin)
  transition: transform ${sliderTransformXFactor === -1 || sliderTransformXFactor === arrLength ? '0' : '0.6s'};
  transform: translateX(
    calc(
      50% -
        (
          (
              ${sliderTransformXFactor === -1
                  ? arrLength
                  : sliderTransformXFactor === arrLength
                  ? -1
                  : sliderTransformXFactor} + 2.5
            ) * (30rem + 2 * 10vw)
        )
    )
  );

  @media screen and (max-width: ${breakpoints.medium}px) {
    transform: translateX(
      calc(
        50% -
          (
            (
                ${sliderTransformXFactor === -1
                    ? arrLength
                    : sliderTransformXFactor === arrLength
                    ? -1
                    : sliderTransformXFactor} + 2.5
              ) * (30rem + 2 * 5vw)
          )
      )
    );
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    transform: translateX(
      calc(
        50% -
          (
            (
                ${sliderTransformXFactor === -1
                    ? arrLength
                    : sliderTransformXFactor === arrLength
                    ? -1
                    : sliderTransformXFactor} + 2.5
              ) * (calc(100vw - 3rem) + 2 * 5vw)
          )
      )
    );
  }
`

const arrowStyle = css`
  z-index: 2;
  background-color: #fff;
  position: absolute;
  bottom: 50%;
  left: calc(50% - (30rem / 2 + 10vw));
  transform: translate(-50%, 50%);
  border-radius: 50%;
  width: 1rem;
  height: 1rem;
  padding: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  box-shadow: rgba(5, 140, 232, 0.1) 0px 0px 20px;

  &:hover {
    box-shadow: rgba(5, 140, 232, 0.2) 0px 0px 30px;
    svg {
      opacity: 0.65;
    }
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    left: calc(50% - (30rem / 2 + 6.75vw));
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    bottom: 0;
    left: calc(50% - 4rem);
  }
`

const rightArrowStyle = css`
  position: absolute;
  left: calc(50% + (30rem / 2 + 10vw));

  @media screen and (max-width: ${breakpoints.medium}px) {
    left: calc(50% + (30rem / 2 + 6.75vw));
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    left: calc(50% + 4rem);
  }
`
